/* eslint-disable @reatom/unit-naming-rule */
import { action, onConnect } from '@reatom/framework'

import {
  Array,
  BigNumber as BigNumber_,
  Order,
  pipe,
  Result,
} from '@blade/std-lib'
import { Network } from '@root/entities/network'
import { fromReusedImageDto } from '@root/entities/pair/atoms/reatom-pair-reused-short'
import { environmentAtom } from '@root/shared'
import { tradeApiClientAtom } from '@root/shared/api/trade-api-client-atom'
import { UnknownError } from '@root/shared/core/Error'
import { reatomRequest } from '@root/shared/lib'

import type {
  CurrencyAddress,
  CurrencyModel,
  ImageHash,
} from '@root/entities/currency/model'
import type * as Pair from '@root/entities/pair/model/pair'

const fetchReusedImagesRequestAction = action(
  async (
    ctx,
    {
      imageHash,
      network,
      address,
    }: {
      imageHash: ImageHash | undefined
      network: Network.Network
      address: CurrencyAddress
    },
  ): Promise<Result.Result<Array<Pair.PairReusedShort>, UnknownError>> => {
    if (!imageHash) {
      return Result.succeed([])
    }

    const imagesHost = ctx.get(environmentAtom).assets.tokenLogosHost
    const { tradeApiClient } = ctx.get(tradeApiClientAtom)

    const response = await ctx.schedule(() =>
      tradeApiClient.services.servicesPoolReusedImageList({
        imageHash,
        network: Network.toDto(network),
        limit: 20,
      }),
    )

    if (!response.ok) {
      return Result.fail(UnknownError.fromUnknown(response.error))
    }

    return Result.succeed(
      pipe(
        response.data,
        Array.filter(
          (row) =>
            row.tokenMeta.address.toLowerCase() !== address.toLowerCase(),
        ),
        Array.map((row) => fromReusedImageDto(row, imagesHost)),
        Array.sort(
          Order.mapInput(BigNumber_.order, (pair: Pair.PairReusedShort) =>
            ctx.get(pair.marketCapUsd).negated(),
          ),
        ),
        Array.compact,
      ),
    )
  },
  'fetchPoolReusedImagesRequestAction',
)

export const reatomReusedImagesOfCurrency = (currency: CurrencyModel) => {
  const hasReusedImages =
    Boolean(currency.reusedImageCount && currency.reusedImageCount > 0) &&
    Boolean(currency.imageHash)

  const requestStateAtom = reatomRequest({
    execAction: fetchReusedImagesRequestAction,
    prepare: (_ctx) => ({
      imageHash: currency.imageHash,
      network: currency.network,
      address: currency.address,
    }),
    name: 'reatomReusedImages',
  })

  onConnect(requestStateAtom, (ctx) => {
    if (hasReusedImages) {
      return requestStateAtom.execute(ctx)
    }
  })

  return requestStateAtom
}

export const reatomReusedImages = (options: {
  imageHash: ImageHash | undefined
  network: Network.Network
  address: CurrencyAddress
}) => {
  const requestStateAtom = reatomRequest({
    execAction: fetchReusedImagesRequestAction,
    prepare: (_ctx) => ({
      imageHash: options.imageHash,
      network: options.network,
      address: options.address,
    }),
    name: 'reatomReusedImages',
  })

  onConnect(requestStateAtom, (ctx) => {
    return requestStateAtom.execute(ctx)
  })

  return requestStateAtom
}
